import { Button } from "components/ui/Button";
import type { Recipient } from "hooks/use-email-compose";
import type { FC } from "react";

export type ReplyConfirmModalProps = {
  isOpen: boolean;
  subject: string;
  body: string;
  recipients: Recipient[];
  fromName: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * 返信メール送信確認モーダル
 * @param {ReplyConfirmModalProps} props
 * @return {JSX.Element}
 */
export const ReplyConfirmModal: FC<ReplyConfirmModalProps> = ({
  isOpen,
  subject,
  body,
  recipients,
  fromName,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const toRecipients = recipients.filter(r => r.type === "to");
  const ccRecipients = recipients.filter(r => r.type === "cc");
  const bccRecipients = recipients.filter(r => r.type === "bcc");

  /**
   * 宛先をフォーマットする
   * @param {Recipient} r - 宛先
   * @return {string} フォーマット済み文字列
   */
  const formatRecipient = (r: Recipient): string =>
    r.name ? `${r.name} <${r.email}>` : r.email;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-4">メール送信の確認</h3>

        <div className="space-y-3">
          {/* 送信者 */}
          <div>
            <span className="text-xs text-gray-500">送信者</span>
            <p className="text-sm">{fromName}</p>
          </div>

          {/* 宛先 (To) */}
          <div>
            <span className="text-xs text-gray-500">宛先 (To)</span>
            {toRecipients.map(r => (
              <p key={r.email} className="text-sm">
                {formatRecipient(r)}
              </p>
            ))}
          </div>

          {/* CC */}
          {ccRecipients.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">CC</span>
              {ccRecipients.map(r => (
                <p key={r.email} className="text-sm">
                  {formatRecipient(r)}
                </p>
              ))}
            </div>
          )}

          {/* BCC */}
          {bccRecipients.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">BCC</span>
              {bccRecipients.map(r => (
                <p key={r.email} className="text-sm">
                  {formatRecipient(r)}
                </p>
              ))}
            </div>
          )}

          {/* 件名 */}
          <div>
            <span className="text-xs text-gray-500">件名</span>
            <p className="text-sm font-semibold">{subject}</p>
          </div>

          {/* 本文プレビュー */}
          <div>
            <span className="text-xs text-gray-500">本文</span>
            <div className="bg-gray-50 rounded-lg p-3 mt-1 max-h-60 overflow-y-auto">
              <p className="text-sm whitespace-pre-wrap">{body}</p>
            </div>
          </div>
        </div>

        <div className="modal-action">
          <Button variant="ghost" onClick={onCancel} type="button">
            キャンセル
          </Button>
          <Button onClick={onConfirm} type="button">
            送信する
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onCancel} />
    </div>
  );
};
